<script setup lang="ts">
import type { Price, ProductDetails } from "mws-ts-v2-sdk";

import { type BidError, BidErrorType } from "~/types/Catalog";
import bidTitle from "~/utils/parseBidTitle";

type Props = {
  currentBid: Price[];
  bidError: BidError | null;
  product: ProductDetails;
  currentUser: {
    hasTopBid: boolean;
    hasBid: boolean;
  };
};

const { currentBid, product, currentUser } = defineProps<Props>();

const { amount: currentPriceAmount } = usePrice(() => currentBid);

const bidTitleTranslation = computed(() => {
  return bidTitle(
    {
      hasBid: product.hasBid,
      isFinished: product.finished,
    },
    {
      isTopBidder: currentUser.hasTopBid,
      hasBid: currentUser.hasBid,
    },
  );
});
</script>

<template>
  <div class="flex flex-col pb-1">
    <span class="uppercase text-platform-text-3 text-sm md:text-lg">
      {{ $t(bidTitleTranslation) }}
    </span>

    <BaseCurrency
      :iso="currentPriceAmount.currencyCode"
      :value="currentPriceAmount.value"
      currency-size="base"
      data-cy="product-current-bid"
      class="text-4xl font-semibold"
    />

    <div
      v-if="
        bidError &&
        bidError.message &&
        bidError.errorType !== BidErrorType.BidLimitExceeded
      "
      data-cy="product-current-bid-bid-error"
    >
      <I18nT
        v-if="bidError.message === 'custom-i18n-bid-error'"
        scope="global"
        class="error-message"
        keypath="errors.nonSpecifiedBidErrorContactSupport"
        tag="p"
      >
        <template
          v-if="bidError.errorCode"
          #errorCode
        >
          ({{ bidError.errorCode }})
        </template>
        <template #contactSupport>
          <BaseCustomerSupportLink class="support-link">
            {{ $t("errors.support") }}
          </BaseCustomerSupportLink>
        </template>
      </I18nT>

      <BaseErrorMessage v-else>
        {{ bidError.message }}
      </BaseErrorMessage>
    </div>
  </div>
</template>
